import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { prepService } from "../../../services/prepService";
import type { PrepExpansionEstimateResponse } from "../../../types/prep";
import { outputTargetIndex } from "../utils/graphAdapter";

export function usePrepJobRun() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [running, setRunning] = useState(false);
  const [expansionDialogOpen, setExpansionDialogOpen] = useState(false);
  const [expansionEstimate, setExpansionEstimate] = useState<PrepExpansionEstimateResponse | null>(null);
  const [pendingJobId, setPendingJobId] = useState<string | null>(null);

  const executeRun = useCallback(async (jobId: string, navigateToRuns = true) => {
    setRunning(true);
    try {
      const job = await prepService.get(jobId);
      if (!outputTargetIndex(job.graph)) {
        throw new Error(t("prep.run.targetIndexRequired"));
      }
      await prepService.requestIndexApproval(jobId);
      await prepService.run(jobId);
      if (navigateToRuns) {
        navigate(`/data-prep/${jobId}/runs`);
      }
      return true;
    } finally {
      setRunning(false);
    }
  }, [navigate, t]);

  const runJob = useCallback(async (jobId: string, navigateToRuns = true) => {
    const estimate = await prepService.estimateExpansion(jobId);
    if (estimate.errors?.length) {
      throw new Error(estimate.errors.join("; "));
    }
    if (estimate.warning) {
      setExpansionEstimate(estimate);
      setPendingJobId(jobId);
      setExpansionDialogOpen(true);
      return { started: false, warning: true };
    }
    await executeRun(jobId, navigateToRuns);
    return { started: true, warning: false };
  }, [executeRun]);

  const confirmExpansionRun = useCallback(async (navigateToRuns = true) => {
    if (!pendingJobId) return;
    setExpansionDialogOpen(false);
    await executeRun(pendingJobId, navigateToRuns);
    setPendingJobId(null);
  }, [pendingJobId, executeRun]);

  const cancelExpansionRun = useCallback(() => {
    setExpansionDialogOpen(false);
    setPendingJobId(null);
    setExpansionEstimate(null);
  }, []);

  return {
    running,
    runJob,
    executeRun,
    expansionDialogOpen,
    expansionEstimate,
    confirmExpansionRun,
    cancelExpansionRun,
  };
}
